import { useState } from "react";
import Button from "../Button/Button";
import Icon from "../Icon/Icon";
import { formatPrice } from "../../utils/formatPrice";
import Modal from "../Modal/Modal";
import MarkAsPurchasedForm from "../Forms/MarkAsPurchasedForm";

export default function ItemCard({
    item,
    onEdit,
    onDelete,
    showPurchaseButton = false,
    onPurchase,
    publicView = false,
}) {
    const imageError = false;
    const [isPurchased, setIsPurchased] = useState(
        Array.isArray(item.purchasedBy) && item.purchasedBy.length > 0
    );
    const [showMarkModal, setShowMarkModal] = useState(false);
    const [showUnmarkModal, setShowUnmarkModal] = useState(false);

    if (!item) return null;

    const handleMarkAsPurchased = (purchaserName) => {
        setIsPurchased(true);
        setShowMarkModal(false);
        if (onPurchase) onPurchase(purchaserName);
    };

    const handleUnmarkConfirmed = () => {
        setIsPurchased(false);
        setShowUnmarkModal(false);
        if (onPurchase) onPurchase(null);
    };

    return (
        <>
            <div
                className={`bg-white shadow rounded-lg p-4 flex flex-col gap-2 h-full hover:shadow-md ${isPurchased ? "opacity-50" : ""}`}
            >
                {/* Image Preview */}
                <div className="w-full h-40 bg-surface rounded flex items-center justify-center overflow-hidden relative">
                    {item.imageUrl && !imageError ? (
                        <a
                            href={item.url || "#"}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="w-full h-full block"
                        >
                            <div
                                className="w-full h-full bg-center bg-cover transition-opacity duration-500"
                                style={{ backgroundImage: `url(${item.imageUrl})` }}
                            />
                        </a>
                    ) : (
                        <span className="absolute">No image</span>
                    )}
                </div>

                {/* Details */}
                <div className="flex-1 flex flex-col gap-1">
                    <h2 className="font-bold text-md truncate pb-2">{item.name}</h2>
                    {!item.isGiftCard && (
                        <>
                            <p className="text-sm">
                                <span className="font-bold">Price:</span>{" "}
                                {item.price != null ? formatPrice(item.price) : "-"}
                            </p>
                            {item.size && <p className="text-sm"><span className="font-bold">Size:</span> {item.size}</p>}
                            {item.color && <p className="text-sm"><span className="font-bold">Color:</span> {item.color}</p>}
                        </>
                    )}
                    {item.notes && (
                        <div className="text-sm mt-1 w-full max-w-full h-20 overflow-y-auto">
                            <span className="font-bold">Notes:</span> {item.notes}
                        </div>
                    )}

                </div>

                {/* Actions */}
                <div className="flex flex-col items-center">
                    {item.url && (
                        <a
                            href={item.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="hover:text-secondary uppercase font-bold text-primary pt-2 pb-1"
                        >
                            {publicView ? "View/Purchase Item" : "View Item"}

                        </a>
                    )}

                    <div className="flex gap-2 mt-2 justify-center">
                        {onEdit && (
                            <Button variant="icon" onClick={() => onEdit(item)}>
                                <Icon icon="edit" />
                            </Button>
                        )}
                        {onDelete && (
                            <Button variant="icon" onClick={() => onDelete(item.id)}>
                                <Icon icon="delete" />
                            </Button>
                        )}
                    </div>

                    {showPurchaseButton && !item.isGiftCard && (
                        <Button
                            type="button"
                            onClick={
                                isPurchased
                                    ? () => setShowUnmarkModal(true)
                                    : () => setShowMarkModal(true)
                            }
                            variant={isPurchased ? "secondary" : "primary"}
                            className="mt-3 w-full"
                        >
                            {isPurchased ? "Unmark as Purchased" : "Mark as Purchased"}
                        </Button>
                    )}

                </div>
            </div>

            {/* Modal for Marking */}
            <Modal isOpen={showMarkModal} onClose={() => setShowMarkModal(false)} size="small">
                <MarkAsPurchasedForm
                    onSubmit={handleMarkAsPurchased}
                    onCancel={() => setShowMarkModal(false)}
                />
            </Modal>

            {/* Modal for Unmarking */}
            <Modal isOpen={showUnmarkModal} onClose={() => setShowUnmarkModal(false)} size="small">
                <div className="space-y-4">
                    <h2 className="text-xl font-semibold">Confirm Unmark</h2>
                    <p>Are you sure you want to make this item available for purchase again?</p>
                    <p className="text-sm">Only do this if you were the person who marked the item as purchased.</p>
                    <div className="flex justify-end gap-3 pt-2">
                        <Button variant="ghost" onClick={() => setShowUnmarkModal(false)}>Cancel</Button>
                        <Button variant="primary" onClick={handleUnmarkConfirmed}>Yes, Unmark</Button>
                    </div>
                </div>
            </Modal>
        </>
    );
}
